import React, { useState } from 'react';
import { ArrowRight, ArrowRightLeft, CheckCircle2, RotateCcw, XCircle } from 'lucide-react';
import { Button, Pill, Spinner } from '../../../components/ui';
import { describeSpecChange, swapActionLabel } from './format';
import { formatPrice } from '../../../utils/currency';
import { ComponentWithHistory, ItemStatus } from './types';
import { ImprovementRef } from './validationEntries';

/**
 * One improvement card: before → after names, every spec the swap changes
 * ("Discharge rate 30C → 40C" — a swap must never rewrite a spec unseen), the engine's rationale,
 * swap / keep actions, and "Ask Robbie why" opening the full grounded
 * reasoning (issue, specs, manufacturer, target price). The mock's per-swap
 * confidence chip is deliberately absent — today's engine scores only the
 * overall recommendation, and we don't invent numbers. Swapped components
 * keep their version history here, with one-click revert.
 */
export interface ImprovementCardProps {
  improvement: ImprovementRef;
  status: ItemStatus;
  isBusy: (key: string) => boolean;
  errorOf: (key: string) => string | undefined;
  component: ComponentWithHistory | null;
  onSwap: (improvement: ImprovementRef) => void;
  onKeep: (key: string) => void;
  onRevert: (componentIndex: number, targetVersionIndex: number, key: string, statusKey: string) => void;
  /** View-only (end user outside their fill window): reasoning shown, swap/revert actions hidden. */
  readOnly?: boolean;
}

export const ImprovementCard: React.FC<ImprovementCardProps> = ({
  improvement,
  status,
  isBusy,
  errorOf,
  component,
  onSwap,
  onKeep,
  onRevert,
  // Effective flag threaded from the validation hook (viewer + handoff,
  // canFill wins) — the card must not consult the wizard-access context
  // itself, or the pending end user's fixes would lock.
  readOnly = false,
}) => {
  const [whyOpen, setWhyOpen] = useState(false);
  const { alt, key, componentIndex } = improvement;
  const best = alt.alternatives[0];
  const swapBusy = isBusy(key);
  const history = component?.replacementHistory || [];
  const currentVersion = component?.currentVersionIndex ?? Math.max(0, history.length - 1);
  const error = errorOf(key) || history.map((_, vIdx) => errorOf(`${key}-revert-${vIdx}`)).find(Boolean);

  if (!best) return null;
  const specChanges = best.specChanges ?? [];

  return (
    <div className={`rounded-[9px] border border-robbie-border-soft bg-surface px-s5 py-[12px] ${status === 'skipped' ? 'opacity-60' : ''}`}>
      <div className="flex flex-wrap items-center gap-s3">
        <span className="text-[12.5px] font-semibold text-ink">{alt.originalComponentName}</span>
        <ArrowRight className="h-[13px] w-[13px] text-ink-muted" />
        <span className="text-[12.5px] font-semibold text-robbie-ink">{best.name}</span>
        {status === 'applied' && (
          <span className="rounded-[4px] bg-success-bg px-[7px] py-[1px] text-[10.5px] font-semibold text-success-ink">
            swapped
          </span>
        )}
        {status === 'skipped' && (
          <span className="rounded-[4px] bg-neutral-bg px-[7px] py-[1px] text-[10.5px] font-semibold text-neutral-ink">
            kept original
          </span>
        )}
      </div>

      {specChanges.length > 0 && (
        <div className="mt-s2 flex flex-wrap gap-s2" aria-label="What this swap changes">
          {specChanges.map((change) => (
            <Pill key={change.key} status="warn">{describeSpecChange(change)}</Pill>
          ))}
        </div>
      )}

      <p className="m-0 mt-s2 text-body-sm text-ink-secondary">{alt.improvementReason}</p>

      <div className="mt-s4 flex flex-wrap items-center gap-s4">
        {status === 'needs_review' && !readOnly && (
          <>
            <Button
              size="sm"
              loading={swapBusy}
              loadingLabel="Swapping…"
              iconLeft={<ArrowRightLeft />}
              onClick={() => onSwap(improvement)}
            >
              {swapActionLabel(alt.originalComponentName, best.name, specChanges)}
            </Button>
            <button
              type="button"
              onClick={() => onKeep(key)}
              className="text-meta font-semibold text-neutral-ink hover:text-ink"
            >
              Keep original
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => setWhyOpen((v) => !v)}
          className="ml-auto text-meta font-semibold text-robbie hover:text-robbie-ink"
        >
          {whyOpen ? 'Hide the details' : 'Ask Robbie why →'}
        </button>
      </div>

      {error && (
        <p className="m-0 mt-s2 flex items-center gap-s2 text-meta font-medium text-danger-ink">
          <XCircle className="h-[11px] w-[11px] shrink-0" />
          {error}
        </p>
      )}

      {whyOpen && (
        <div className="mt-s4 flex flex-col gap-s3 rounded-control border border-robbie-border-soft bg-ai-chip px-s4 py-s4">
          <p className="m-0 text-body-sm text-ink-body">
            <span className="font-semibold text-ink">The issue:</span> {alt.issue}
          </p>
          <p className="m-0 text-body-sm text-ink-body">
            <span className="font-semibold text-ink">Why {best.name} is better:</span> {best.reason}
          </p>
          {best.specifications && (
            <p className="m-0 text-meta text-ink-secondary">
              Specs: <span className="text-ink-body">{best.specifications}</span>
            </p>
          )}
          <div className="flex flex-wrap gap-s5">
            {best.manufacturer && (
              <span className="text-meta text-ink-secondary">
                Manufacturer: <span className="text-ink-body">{best.manufacturer}</span>
              </span>
            )}
            {best.targetPrice != null && best.targetPrice > 0 && (
              <span className="text-meta text-ink-secondary">
                Target price: <span className="font-mono text-mono-data text-ink-body">{formatPrice(best.targetPrice, best.currency || 'INR')}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {history.length > 1 && componentIndex !== -1 && (
        <div className="mt-s4 border-t border-line-divider pt-s3">
          <p className="m-0 mb-s2 text-table-header uppercase text-ink-muted">Version history</p>
          <div className="flex flex-col gap-s2">
            {history.map((entry, vIdx) => {
              const isCurrent = vIdx === currentVersion;
              const revertKey = `${key}-revert-${vIdx}`;
              return (
                <div key={vIdx} className="flex items-center gap-s3 text-meta">
                  <span className="font-mono text-mono-data text-ink-muted">v{entry.version}</span>
                  <span className="min-w-0 truncate text-ink-body">{entry.name}</span>
                  {isCurrent ? (
                    <span className="ml-auto inline-flex shrink-0 items-center gap-[3px] font-semibold text-success-ink">
                      <CheckCircle2 className="h-[11px] w-[11px]" /> current
                    </span>
                  ) : readOnly ? (
                    <span className="ml-auto shrink-0 text-ink-muted">earlier version</span>
                  ) : (
                    <button
                      type="button"
                      disabled={isBusy(revertKey)}
                      onClick={() => onRevert(componentIndex, vIdx, revertKey, key)}
                      className="ml-auto inline-flex shrink-0 items-center gap-[3px] font-semibold text-neutral-ink hover:text-ink disabled:opacity-50"
                    >
                      {isBusy(revertKey) ? (
                        <Spinner size="sm" />
                      ) : (
                        <RotateCcw className="h-[11px] w-[11px]" />
                      )}
                      Revert to this
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImprovementCard;
